import { useMemo } from 'react';

import { useCrowdfundingProvider } from '#Context/crowdfunding/Crowdfunding.provider.js';
import { useDonorTranslations } from '#Context/crowdfunding/hooks.js';
import { useListCampaignPrize } from '#api/index.js';
import {
  useShowConsumerCampaignById,
  useShowConsumerCampaignSettingsById,
} from '@givebacks/api/store';
import { useBreakPoints } from 'core/Hooks';
import { Carousel, Stack, Title, TransitionElement } from 'ui';

import { PrizeCardModal } from '../../../components';

const PrizeLayout = ({
  title,
  type,
  campaignId,
  causeId,
}: {
  title: string;
  type?: 'team' | 'participant';
  campaignId?: string;
  causeId?: string;
}) => {
  const { isXSmallScreen, isMediumScreen } = useBreakPoints();
  const { data: prizes } = useListCampaignPrize({
    campaignId,
    causeId,
    queryOptions: {
      searches: {
        peer_type: {
          value: type,
        },
      },
    },
  });

  const prizeSlides = useMemo(
    () =>
      prizes?.map((prize) => (
        <Carousel.Slide key={prize.uuid}>
          <PrizeCardModal prize={prize} />
        </Carousel.Slide>
      )),
    [prizes]
  );

  return (
    <TransitionElement mounted={!!prizes?.length} transition={'fade'}>
      <Stack mih={296} gap={0}>
        <Title order={2}>{title}</Title>
        <Carousel
          slideSize={{ base: '100%', xs: '50%', md: '225px' }}
          slideGap={{ base: 'xs', sm: 'md' }}
          align={isXSmallScreen ? 'center' : 'start'}
          dragFree={true}
          containScroll="trimSnaps"
          styles={{
            viewport: { padding: 16, marginLeft: -16 },
            control: {
              '&[data-inactive]': {
                opacity: 0,
                cursor: 'default',
              },
            },
          }}
          withControls={
            !isMediumScreen && prizeSlides?.length && prizeSlides?.length > 3 ? true : false
          }
          withIndicators={isMediumScreen ? true : false}
        >
          {prizeSlides}
        </Carousel>
      </Stack>
    </TransitionElement>
  );
};

export const Prizes = () => {
  const { causeId, campaignId } = useCrowdfundingProvider();

  const { data: campaignSettings } = useShowConsumerCampaignSettingsById({
    causeId,
    campaignId,
  });
  const { t } = useDonorTranslations();
  const { data: campaign } = useShowConsumerCampaignById({ causeId, campaignId });
  return (
    <Stack gap={'xl'} maw={{ base: '100%', lg: 684 }}>
      {campaignSettings?.isParticipantPrizesEnabled && (
        <PrizeLayout
          title={`${t('participant')} Prizes`}
          type={'participant'}
          campaignId={campaign?.uuid}
          causeId={campaign?.cause_id}
        />
      )}
      {campaignSettings?.isTeamPrizesEnabled && (
        <PrizeLayout
          title={`${t('team')} Prizes`}
          type={'team'}
          campaignId={campaign?.uuid}
          causeId={campaign?.cause_id}
        />
      )}
    </Stack>
  );
};
